import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, Clock, CheckCircle, XCircle, Send, Trash2, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';

const MessageCenter = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    loadMessages();
    loadStats();
  }, [filterStatus]);

  const loadMessages = async () => {
    try {
      const params = filterStatus ? { status: filterStatus } : {};
      const response = await axiosInstance.get('/messages/admin/all', { params });
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Fehler beim Laden der Nachrichten');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axiosInstance.get('/messages/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) {
      toast.error('Bitte Antwort eingeben');
      return;
    }

    try {
      await axiosInstance.post(`/messages/admin/${selectedMessage.id}/reply`, { reply_text: replyText });
      toast.success('Antwort gesendet!');
      setReplyText('');
      loadMessages();
      try {
        const messageResponse = await axiosInstance.get(`/messages/admin/${selectedMessage.id}`);
        setSelectedMessage(messageResponse.data);
      } catch (e) {
        // refresh failed, but reply succeeded
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Fehler beim Senden der Antwort');
    }
  };

  const updateStatus = async (messageId, newStatus) => {
    try {
      await axiosInstance.put(`/messages/admin/${messageId}/status`, { status: newStatus });
      toast.success('Status aktualisiert!');
      loadMessages();
      loadStats();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Nachricht wirklich löschen?')) return;

    try {
      await axiosInstance.delete(`/messages/admin/${messageId}`);
      toast.success('Nachricht gelöscht!');
      setSelectedMessage(null);
      loadMessages();
      loadStats();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      new: { label: 'Neu', color: 'bg-blue-100 text-blue-800', icon: Mail },
      in_progress: { label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      answered: { label: 'Beantwortet', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      closed: { label: 'Geschlossen', color: 'bg-gray-100 text-gray-800', icon: XCircle }
    };

    const { label, color, icon: Icon } = config[status] || config.new;

    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Nachrichten-Center</h1>
        <p className="text-gray-600">Verwalten Sie Kundennachrichten</p>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-gray-600">Gesamt</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.new}</p>
              <p className="text-sm text-gray-600">Neu</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats.in_progress}</p>
              <p className="text-sm text-gray-600">In Bearbeitung</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.answered}</p>
              <p className="text-sm text-gray-600">Beantwortet</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-600">{stats.closed}</p>
              <p className="text-sm text-gray-600">Geschlossen</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="mb-6 flex gap-2">
        <Button
          variant={filterStatus === '' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('')}
          size="sm"
        >
          Alle
        </Button>
        <Button
          variant={filterStatus === 'new' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('new')}
          size="sm"
        >
          Neu
        </Button>
        <Button
          variant={filterStatus === 'in_progress' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('in_progress')}
          size="sm"
        >
          In Bearbeitung
        </Button>
        <Button
          variant={filterStatus === 'answered' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('answered')}
          size="sm"
        >
          Beantwortet
        </Button>
      </div>

      {/* Messages List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1 space-y-3 max-h-[600px] overflow-y-auto">
          {messages.map((message) => (
            <Card
              key={message.id}
              className={`cursor-pointer transition-all ${
                selectedMessage?.id === message.id
                  ? 'border-blue-500 shadow-lg'
                  : 'hover:shadow-md'
              }`}
              onClick={() => setSelectedMessage(message)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold">{message.customer_name}</p>
                    <p className="text-sm text-gray-600">{message.customer_email}</p>
                  </div>
                  {getStatusBadge(message.status)}
                </div>
                <p className="font-medium text-sm mb-1">{message.subject}</p>
                <p className="text-sm text-gray-600 line-clamp-2">{message.message}</p>
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(message.created_at).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </CardContent>
            </Card>
          ))}

          {messages.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p>Keine Nachrichten gefunden</p>
            </div>
          )}
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2">
          {selectedMessage ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{selectedMessage.subject}</CardTitle>
                  <div className="flex gap-2">
                    <select
                      value={selectedMessage.status}
                      onChange={(e) => updateStatus(selectedMessage.id, e.target.value)}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value="new">Neu</option>
                      <option value="in_progress">In Bearbeitung</option>
                      <option value="answered">Beantwortet</option>
                      <option value="closed">Geschlossen</option>
                    </select>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteMessage(selectedMessage.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-b pb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Von: <strong>{selectedMessage.customer_name}</strong> ({selectedMessage.customer_email})
                  </p>
                  <p className="text-sm text-gray-600">
                    {new Date(selectedMessage.created_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded">
                  <p className="whitespace-pre-line">{selectedMessage.message}</p>
                </div>

                {/* Replies */}
                {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-semibold">Antworten:</h3>
                    {selectedMessage.replies.map((reply) => (
                      <div key={reply.id} className="bg-blue-50 p-4 rounded">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-semibold">{reply.admin_name}</p>
                          <p className="text-xs text-gray-600">
                            {new Date(reply.created_at).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <p className="whitespace-pre-line">{reply.reply_text}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Form */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Antworten:</h3>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Ihre Antwort..."
                    rows={4}
                    className="mb-3 rounded-none border-[#D4C4B0]"
                  />
                  <Button onClick={handleReply} className="rounded-none bg-[#E3F2FD] text-[#2196F3] hover:bg-[#BBDEFB] border-0">
                    <Send className="h-4 w-4 mr-2" />
                    Antwort senden
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>Wählen Sie eine Nachricht aus, um Details anzuzeigen</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageCenter;